import ResetIcon from "@mui/icons-material/LoopOutlined";
import PauseCircleIcon from "@mui/icons-material/PauseOutlined";
import PlayCircleIcon from "@mui/icons-material/PlayArrowOutlined";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { formatInTimeZone } from "date-fns-tz";
import { useCallback, useMemo } from "react";
import { CircularProgress } from "@mui/material";

import { useWidgetTimezone } from "@/features/shared/widget-context/widget-runtime-context";
import {
  TimeRangeDimension,
  TimeRangeDimensionValue,
} from "@/lib/dimensions/time-range-dimension";
import { MapLayersController } from "@/lib/map-layers";
import {
  PlayerWrapper,
  StyledSlider,
} from "@/features/shared/widgets/map/controls/player-control.styles";

type PlayerProps = MapLayersController;

const Player = ({
  dimensions,
  currentDimensionValues,
  setDimensionValue,
  isLoading,
  isPlaying,
  playInLoopEnabled,
  togglePlaying,
  togglePlayInLoop,
}: PlayerProps) => {
  const dimension = useMemo(
    () => dimensions.find((d) => d instanceof TimeRangeDimension),
    [dimensions],
  );
  const currentDimensionValue = useMemo(() => {
    return dimension
      ? (currentDimensionValues[dimension.id] as TimeRangeDimensionValue)
      : undefined;
  }, [dimension, currentDimensionValues]);

  if (!dimension || !currentDimensionValue) return null;

  return (
    <PlayerInner
      isPlaying={isPlaying}
      isLoading={isLoading}
      playInLoopEnabled={playInLoopEnabled}
      dimension={dimension}
      currentDimensionValue={currentDimensionValue}
      setDimensionValue={(value) => setDimensionValue(dimension.id, value)}
      togglePlayInLoop={togglePlayInLoop}
      togglePlaying={togglePlaying}
    />
  );
};

type PlayerInnerProps = {
  isPlaying: boolean;
  isLoading: boolean;
  playInLoopEnabled: boolean;
  dimension: TimeRangeDimension;
  currentDimensionValue: TimeRangeDimensionValue;
  togglePlayInLoop: () => void;
  togglePlaying: () => void;
  setDimensionValue: (value: TimeRangeDimensionValue) => void;
};

const PlayerInner = ({
  isPlaying,
  isLoading,
  playInLoopEnabled,
  dimension,
  currentDimensionValue,
  togglePlayInLoop,
  togglePlaying,
  setDimensionValue,
}: PlayerInnerProps) => {
  const tz = useWidgetTimezone();
  const timezone = tz?.name === "UTC" ? tz.offset : (tz?.name ?? "");

  const valueIndex = useMemo(() => {
    return dimension.values.findIndex(
      (value) => value.timestamp === currentDimensionValue.timestamp,
    );
  }, [dimension, currentDimensionValue]);

  const maxValueIndex = useMemo(() => {
    const length = dimension.values.length;
    return length > 0 ? length - 1 : 0;
  }, [dimension]);

  const onChange = useCallback(
    (_: Event, index: number | number[]) => {
      index = Array.isArray(index) ? index[0] : index;
      setDimensionValue(dimension.values[index]);
    },
    [dimension],
  );

  return (
    <PlayerWrapper>
      <Stack
        direction="row"
        role="player"
        alignItems="center"
        justifyContent="center"
        className="player-stack"
        sx={{ maxWidth: "100%" }}
      >
        <IconButton
          title={`Toggle loop ${playInLoopEnabled ? "off" : "on"}`}
          onClick={togglePlayInLoop}
        >
          <ResetIcon
            fontSize="medium"
            color={playInLoopEnabled ? "primary" : undefined}
            className={
              playInLoopEnabled ? "player-icon" : "player-icon player-icon--inactive"
            }
          />
        </IconButton>
        <IconButton
          aria-label={isPlaying ? "Pause" : "Play"}
          disabled={isLoading}
          onClick={togglePlaying}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : isPlaying ? (
            <PauseCircleIcon fontSize="medium" className="player-icon" />
          ) : (
            <PlayCircleIcon fontSize="medium" className="player-icon" />
          )}
        </IconButton>

        {currentDimensionValue && (
          <div className="player-time">
            {formatInTimeZone(currentDimensionValue.timestamp, timezone, "HH:mm")}
            <br />
            <span className="player-time--small">
              {formatInTimeZone(currentDimensionValue.timestamp, timezone, "dd/MM")}
            </span>
          </div>
        )}
        <StyledSlider
          value={valueIndex}
          max={maxValueIndex}
          onChange={onChange}
          className="player-slider"
        />
      </Stack>
    </PlayerWrapper>
  );
};

export default Player;
